import React, { useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { formatUSDT, shortenAddress, bpsToPercent } from '../../utils/format';
import { Spinner } from '../common/Loader';

// Safety limit — a very wide tree could otherwise DDoS the RPC.
const MAX_FETCH_PER_LEVEL = 500;

function gradFor(addr) {
  if (!addr) return 'linear-gradient(135deg, #3B4A45, #1E2A26)';
  const h = parseInt(addr.slice(2, 8), 16) % 360;
  return `linear-gradient(135deg, hsl(${h}, 70%, 55%), hsl(${(h + 60) % 360}, 70%, 45%))`;
}

function initials(addr) {
  if (!addr) return '—';
  return addr.slice(2, 4).toUpperCase();
}

/**
 * Walk the sponsor tree client-side to build the list of user IDs at each level.
 * Level 1 = direct referrals. Level N = union of direct referrals of every
 * user at level N-1.
 */
async function fetchLevelUsers(storage, rootId, targetLevel, onProgress) {
  // Start at level 1
  let currentLevelIds = (await storage.getDirects(rootId)).map((n) => n.toString());
  let level = 1;

  while (level < targetLevel && currentLevelIds.length > 0) {
    onProgress?.({ level, count: currentLevelIds.length });

    // Cap the fan-out so we don't fire 10k calls
    const seed = currentLevelIds.slice(0, MAX_FETCH_PER_LEVEL);

    // Fan out: fetch all directs of every user in the current level, in parallel
    const results = await Promise.all(
      seed.map((id) => storage.getDirects(id).catch(() => []))
    );

    // Flatten + dedup (in case of any tree oddity)
    const seen = new Set();
    const next = [];
    for (const list of results) {
      for (const bn of list) {
        const s = bn.toString();
        if (!seen.has(s)) {
          seen.add(s);
          next.push(s);
        }
      }
    }
    currentLevelIds = next;
    level++;
  }

  return currentLevelIds.slice(0, MAX_FETCH_PER_LEVEL);
}

export default function LevelExplorer({ rootId, levelIndex, bps, directsRequired, eligible }) {
  const { read } = useContracts();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState(null);   // list of full user objects
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);
  const [truncated, setTruncated] = useState(false);

  const level = levelIndex + 1;
  const need = Number(directsRequired);

  async function loadLevel() {
    setLoading(true);
    setError('');
    setProgress(null);
    setTruncated(false);
    try {
      const ids = await fetchLevelUsers(read.storage, rootId, level, (p) => setProgress(p));

      if (ids.length >= MAX_FETCH_PER_LEVEL) setTruncated(true);

      // Fetch each user's basic info in parallel (batched to avoid RPC flooding)
      const BATCH = 50;
      const fetched = [];
      for (let i = 0; i < ids.length; i += BATCH) {
        setProgress({ level, count: ids.length, fetched: i });
        const batch = ids.slice(i, i + BATCH);
        const res = await Promise.all(
          batch.map((id) =>
            read.storage.getUser(id).then((u) => ({ id, u })).catch(() => null)
          )
        );
        fetched.push(...res.filter(Boolean));
      }
      setUsers(fetched);
    } catch (err) {
      setError(err?.message?.slice(0, 100) || 'Failed to load level');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }

  function toggle() {
    if (!open) {
      setOpen(true);
      if (users === null && !loading) loadLevel();
    } else {
      setOpen(false);
    }
  }

  const isEligible = eligible;

  return (
    <div className={`level-cell ${isEligible ? 'eligible' : 'locked'} ${open ? 'open' : ''}`}>
      <button className="level-cell-toggle" onClick={toggle} aria-expanded={open}>
        <div className="lvl-title">
          <span className="lvl-num">Level {level}</span>
          <span className="lvl-pct">{bpsToPercent(bps)}</span>
        </div>
        <div className="lvl-req">
          {need === 0 ? 'No criteria' : `Requires ${need} direct${need === 1 ? '' : 's'}`}
          {isEligible && <span style={{ color: 'var(--accent)', marginLeft: 8 }}>✓ Unlocked</span>}
        </div>
        <div className="lvl-chevron">{open ? '▲' : '▼'}</div>
      </button>

      {open && (
        <div className="level-cell-body">
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center' }}>
              <Spinner size="lg" />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
                {progress ? (
                  progress.fetched !== undefined
                    ? `Loading ${progress.fetched}/${progress.count}...`
                    : `Walking level ${progress.level} (${progress.count} nodes)...`
                ) : 'Loading...'}
              </div>
            </div>
          ) : error ? (
            <div style={{ color: 'var(--error)', fontSize: 12, padding: 12 }}>{error}</div>
          ) : users && users.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No users at level {level} yet.
            </div>
          ) : users ? (
            <>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 11, color: 'var(--text-muted)', marginBottom: 10,
                textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700,
              }}>
                <span>{users.length} user{users.length === 1 ? '' : 's'} at level {level}</span>
                {truncated && <span style={{ color: 'var(--warning)' }}>⚠ Truncated at {MAX_FETCH_PER_LEVEL}</span>}
              </div>
              <div className="level-users-list">
                {users.map(({ id, u }) => {
                  const qualifying = u.totalInvestmentUSDT?.gt?.(0);
                  return (
                    <div key={id} className="level-user-row">
                      <div className="level-user-avatar" style={{ background: gradFor(u.wallet) }}>
                        {initials(u.wallet)}
                      </div>
                      <div className="level-user-info">
                        <div className="lu-id">ID {id}</div>
                        <div className="lu-addr">{shortenAddress(u.wallet, 4)}</div>
                      </div>
                      <div className="level-user-meta">
                        <div className={`lu-status ${qualifying ? 'q' : 'p'}`}>
                          {qualifying ? '✓ Purchased' : '⌛ Pending'}
                        </div>
                        {qualifying && (
                          <div className="lu-invested">
                            {formatUSDT(u.totalInvestmentUSDT, 0)} USDT
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
